import type { Repositories } from '@/infrastructure/repositories/RepositoryInterfaces.js';
import type { AssetType } from '@k-ucap/shared';

export interface RecentAsset {
  id: string;
  type: AssetType;
  name: string;
  projectId: string;
  updatedAt: string;
  href: string;
}

const ASSET_LIMIT = 5;

export class RecentAssetsService {
  constructor(private readonly repositories: Repositories) {}

  async loadRecentAssets(projectId: string): Promise<RecentAsset[]> {
    const [
      simulationTests,
      simulationRuns,
      testSuites,
      reports,
      connectionProfiles,
      messageTemplates,
      parameterSets,
      datasets,
      sessions,
    ] = await Promise.all([
      this.repositories.simulationTestRepository.findByProjectId(projectId),
      this.repositories.simulationRunRepository.findByProjectId(projectId),
      this.repositories.testSuiteRepository.findByProjectId(projectId),
      this.repositories.reportRepository.findByProjectId(projectId),
      this.repositories.connectionProfileRepository.findByProjectId(projectId),
      this.repositories.messageTemplateRepository.findByProjectId(projectId),
      this.repositories.parameterSetRepository.findByProjectId(projectId),
      this.repositories.datasetRepository.findByProjectId(projectId),
      this.repositories.sessionRepository.findByProjectId(projectId),
    ]);

    const assets: RecentAsset[] = [
      ...simulationTests.map((t) => ({
        id: t.id,
        type: 'SIMULATION_TEST' as AssetType,
        name: t.name,
        projectId: t.projectId,
        updatedAt: t.updatedAt,
        href: `/scenario?test=${t.id}`,
      })),
      ...simulationRuns.map((r) => ({
        id: r.id,
        type: 'REPORT' as AssetType,
        name: r.name,
        projectId: r.projectId,
        updatedAt: r.updatedAt,
        href: `/reports?run=${r.id}`,
      })),
      ...testSuites.map((s) => ({
        id: s.id,
        type: 'TEST_SUITE' as AssetType,
        name: s.name,
        projectId: s.projectId,
        updatedAt: s.updatedAt,
        href: `/scenario?suite=${s.id}`,
      })),
      ...reports.map((r) => ({
        id: r.id,
        type: 'REPORT' as AssetType,
        name: r.title,
        projectId: r.projectId,
        updatedAt: r.updatedAt,
        href: `/reports?report=${r.id}`,
      })),
      ...connectionProfiles.map((p) => ({
        id: p.id,
        type: 'CONNECTION_PROFILE' as AssetType,
        name: p.name,
        projectId: p.projectId,
        updatedAt: p.updatedAt,
        href: `/integration?profile=${p.id}`,
      })),
      ...messageTemplates.map((t) => ({
        id: t.id,
        type: 'MESSAGE_TEMPLATE' as AssetType,
        name: t.name,
        projectId: t.projectId,
        updatedAt: t.updatedAt,
        href: `/generator?assetType=MESSAGE_TEMPLATE&assetId=${t.id}`,
      })),
      ...parameterSets.map((p) => ({
        id: p.id,
        type: 'PARAMETER_SET' as AssetType,
        name: p.name,
        projectId: p.projectId,
        updatedAt: p.updatedAt,
        href: `/generator?assetType=PARAMETER_SET&assetId=${p.id}`,
      })),
      ...datasets.map((d) => ({
        id: d.id,
        type: 'DATASET' as AssetType,
        name: d.name,
        projectId: d.projectId,
        updatedAt: d.updatedAt,
        href: `/generator?assetType=DATASET&assetId=${d.id}`,
      })),
      ...sessions.map((s) => ({
        id: s.id,
        type: 'SESSION' as AssetType,
        name: `Session ${new Date(s.startedAt).toLocaleString()}`,
        projectId: s.projectId,
        updatedAt: s.updatedAt,
        href: `/scenario?sessionId=${s.id}`,
      })),
    ];

    return assets
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, ASSET_LIMIT);
  }
}

export const createRecentAssetsService = (repositories: Repositories): RecentAssetsService =>
  new RecentAssetsService(repositories);
